import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Icon } from '../Icon';

const collapsibleSectionHeaderVariants = cva('sticky top-0 z-[1] flex items-center gap-2 bg-white', {
  variants: {
    size: {
      default: 'justify-between px-2.5 py-2',
      // Title, badge and actions packed left in one row
      compact: 'justify-start px-2 py-1',
    },
  },
  defaultVariants: { size: 'default' },
});

const collapsibleSectionToggleVariants = cva('flex min-w-0 cursor-pointer items-center text-left focus-ring', {
  variants: {
    size: {
      default: 'grow gap-1',
      compact: 'gap-2',
    },
  },
  defaultVariants: { size: 'default' },
});

const collapsibleSectionTitleVariants = cva('truncate', {
  variants: {
    size: {
      default: 'text-base font-medium text-grey-90',
      compact: 'font-noto text-xs font-semibold leading-[1.4] text-grey-80',
    },
  },
  defaultVariants: { size: 'default' },
});

const CHEVRON_SIZE = { default: 16, compact: 14 } as const;

type CollapsibleSectionProps = React.ComponentProps<'div'> &
  VariantProps<typeof collapsibleSectionHeaderVariants> & {
    title: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Rendered after the title inside the toggle `<button>`, e.g. a count or icon, so it must not be interactive. */
    titleAdornment?: React.ReactNode;
    /** Rendered in the header's trailing cluster, e.g. a category chip; right-aligned unless `compact`. */
    badge?: React.ReactNode;
    /** Rendered after the badge in the trailing cluster; consumer-owned so it stays app-specific. */
    actions?: React.ReactNode;
    /** When true, paints the blue hover-ring overlay over the header and body. */
    hovered?: boolean;
    children: React.ReactNode;
    /** Testid for the chevron/title toggle button. */
    toggleTestId?: string;
    'data-testid'?: string;
  };

function CollapsibleSection({
  title,
  open,
  onOpenChange,
  titleAdornment,
  badge,
  actions,
  hovered = false,
  children,
  toggleTestId,
  size,
  className,
  'data-testid': dataTestId,
  ...props
}: CollapsibleSectionProps) {
  const contentId = React.useId();

  return (
    <div
      {...props}
      data-slot="collapsible-section"
      data-testid={dataTestId}
      className={cn('relative flex flex-col border-b border-grey-30 bg-white', className)}
    >
      {/* Overlay sits above the sticky header (z-2 > z-1) so the ring paints over solid row backgrounds. */}
      {hovered && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[2] rounded-[inherit] ring-1 ring-inset ring-blue-40"
        />
      )}
      <div className={collapsibleSectionHeaderVariants({ size })}>
        <button
          type="button"
          data-testid={toggleTestId}
          aria-expanded={open}
          aria-controls={open ? contentId : undefined}
          onClick={() => onOpenChange(!open)}
          className={collapsibleSectionToggleVariants({ size })}
        >
          <Icon
            icon={open ? 'chevronUp' : 'chevronRight'}
            size={CHEVRON_SIZE[size ?? 'default']}
            className="shrink-0"
          />
          <span className={collapsibleSectionTitleVariants({ size })}>{title}</span>
          {titleAdornment}
        </button>
        {(badge || actions) && (
          // Sibling of the toggle so interactive badge/actions aren't nested inside the button.
          <div className="flex shrink-0 items-center gap-2">
            {badge}
            {actions}
          </div>
        )}
      </div>
      {open && <div id={contentId}>{children}</div>}
    </div>
  );
}

export { CollapsibleSection };
export type { CollapsibleSectionProps };
